import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { BasicTable, ContentCard } from '@/components/customComponents'
import { lineChartOptions, pieChartOptions } from '@/constants/CHART_CONSTANTS'
import { toast } from '@/hooks'
import usePaymentType from '@/hooks/clarityLens/salesLens/paymentType/usePaymentType'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxSalesLensFilter,
} from '@/store'
import { dateToString } from '@/utils/utils'
import { useEffect } from 'react'

interface PaymentTypeTabProps {
  itemName: string
}

export const PaymentTypeTab = ({ itemName }: PaymentTypeTabProps) => {
  const { t } = useTranslation()
  const { selectedBranch, date, sizeFilter, temperatureFilter } =
    useReduxSalesLensFilter()
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('sales-lens-item')
  )

  const {
    paymentTypeData,
    PAYMENT_TYPE_COLUMNS,
    paymentTypeChartData,
    paymentMethodDistributionChartData,
    driveThruVsInStoreChartData,
    driveThruVsInStoreLineChartData,
    loading: paymentTypeLoading,
    error: paymentTypeError,
    refetch: paymentTypeRefetch,
  } = usePaymentType(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName,
    sizeFilter,
    temperatureFilter
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing payment type items...')
      paymentTypeRefetch()
    }
  }, [refreshTrigger, paymentTypeRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (paymentTypeError) {
      toast({
        title: t('common.errors.serverError'),
        description: paymentTypeError?.message,
        variant: 'destructive',
      })
    }
  }, [paymentTypeError])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title={t('clarityLens.salesLens.charts.paymentMethodDistribution')}
          loading={paymentTypeLoading}
          isEmpty={
            !paymentMethodDistributionChartData ||
            Object.keys(paymentMethodDistributionChartData.data || {})
              .length === 0
          }
        >
          {paymentMethodDistributionChartData && (
            <ReactEcharts
              option={getPieChartData(
                paymentMethodDistributionChartData?.data,
                paymentMethodDistributionChartData?.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.paymentType')}
          loading={paymentTypeLoading}
          isEmpty={
            !paymentTypeChartData ||
            Object.keys(paymentTypeChartData.data || {}).length === 0
          }
        >
          {paymentTypeChartData && (
            <ReactEcharts
              option={getPieChartData(
                paymentTypeChartData?.data,
                paymentTypeChartData?.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.salesLens.charts.driveThruVsInStore')}
          loading={paymentTypeLoading}
          isEmpty={
            !driveThruVsInStoreChartData ||
            Object.keys(driveThruVsInStoreChartData.data || {}).length === 0
          }
        >
          {driveThruVsInStoreChartData && (
            <ReactEcharts
              option={getPieChartData(
                driveThruVsInStoreChartData.data,
                driveThruVsInStoreChartData.labels
              )}
            />
          )}
        </ContentCard>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ContentCard
          title={t('clarityLens.salesLens.charts.driveThruVsInStoreBreakdown')}
          loading={paymentTypeLoading}
          isEmpty={
            !driveThruVsInStoreLineChartData ||
            !driveThruVsInStoreLineChartData.rawData.length ||
            !driveThruVsInStoreLineChartData.xAxisNames.length
          }
          isBeta
        >
          {driveThruVsInStoreLineChartData && (
            <ReactEcharts
              option={getLineChartData(driveThruVsInStoreLineChartData)}
            />
          )}
        </ContentCard>

        <BasicTable
          title={t('clarityLens.salesLens.charts.paymentTypeOverview')}
          data={paymentTypeData}
          columns={PAYMENT_TYPE_COLUMNS}
          loading={paymentTypeLoading}
        />
      </div>
    </div>
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  return pieChartOptions(data, labels)
}

const getLineChartData = (chartData: {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}) => {
  return lineChartOptions(
    chartData.rawData,
    chartData.seriesNames,
    chartData.xAxisNames,
    'number'
  )
}
